/**
 * Maps a committed saved address onto the existing restaurant-scoped
 * destination store. Does not own address selection, GPS, serviceability,
 * checkout, or persistence.
 *
 * Destination remains { restaurantId, latitude, longitude }.
 * Address identity stays in the address book.
 */

import type { Address } from "@/models/user"
import {
  clearDestinationCoordinates,
  getDestinationCoordinates,
  setDestinationCoordinates,
} from "@/domain/destination/destination-coordinates.store"

export type SavedAddressCoordinatePair = {
  latitude: number
  longitude: number
}

let lastFingerprint: string | null = null

function requireRestaurantId(restaurantId: string): string | null {
  const rid = restaurantId.trim()
  return rid || null
}

function serializeCoordinate(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "null"
  return String(value)
}

function fingerprint(restaurantId: string, address: Address | null): string {
  return [
    restaurantId,
    address?.address_id != null ? String(address.address_id) : "null",
    serializeCoordinate(address?.location_latitude),
    serializeCoordinate(address?.location_longitude),
  ].join("|")
}

/**
 * Complete finite pair only. Partial or missing coordinates are unavailable.
 * Does not manufacture 0 or copy a lone sibling value.
 */
export function readSavedAddressCoordinates(
  address: Address | null | undefined
): SavedAddressCoordinatePair | null {
  if (!address) return null
  const latitude = address.location_latitude
  const longitude = address.location_longitude
  if (typeof latitude !== "number" || typeof longitude !== "number") return null
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  return { latitude, longitude }
}

/**
 * True only when the active restaurant dest pin is the selected address pair.
 * GPS candidates and mismatched pins are not a saved location.
 */
export function savedAddressMatchesActiveDestination(
  restaurantId: string,
  address: Address | null | undefined
): boolean {
  const rid = requireRestaurantId(restaurantId)
  if (!rid) return false
  const dest = getDestinationCoordinates(rid)
  const pair = readSavedAddressCoordinates(address)
  if (!dest || !pair) return false
  return (
    serializeCoordinate(dest.latitude) === serializeCoordinate(pair.latitude) &&
    serializeCoordinate(dest.longitude) === serializeCoordinate(pair.longitude)
  )
}

function writeDestination(restaurantId: string, address: Address | null): void {
  const pair = readSavedAddressCoordinates(address)
  if (!pair) {
    clearDestinationCoordinates(restaurantId)
    return
  }
  try {
    setDestinationCoordinates({
      restaurantId,
      latitude: pair.latitude,
      longitude: pair.longitude,
    })
  } catch {
    clearDestinationCoordinates(restaurantId)
  }
}

function apply(
  restaurantId: string,
  address: Address | null,
  force: boolean
): boolean {
  const rid = requireRestaurantId(restaurantId)
  if (!rid) return false

  const next = fingerprint(rid, address)
  if (!force && next === lastFingerprint) return false

  lastFingerprint = next
  writeDestination(rid, address)
  return true
}

/**
 * Lifecycle / refetch path. Skips when restaurant, address id, and
 * coordinate values are unchanged so GPS overrides are not churned away.
 */
export function syncSavedAddressDestination(
  restaurantId: string,
  address: Address | null
): boolean {
  return apply(restaurantId, address, false)
}

/**
 * Explicit committed selection. Latest explicit destination source wins,
 * including re-selecting the same address after a GPS override.
 */
export function commitSavedAddressDestination(
  restaurantId: string,
  address: Address | null
): boolean {
  return apply(restaurantId, address, true)
}

/** Tenant switch, logout, and tests. */
export function resetSavedAddressDestinationResolver(): void {
  lastFingerprint = null
}

export function resetSavedAddressDestinationResolverForTests(): void {
  resetSavedAddressDestinationResolver()
}
